import React, { useState } from 'react';
import {
  CategoryMealTag,
  useToast,
  useTranslator,
  cloudflareImage,
} from '@front-activerse/common-components';
import { Button, Col, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useAppContext } from '@front-activerse/common-components';

interface ICookbookMealItem {
  addToFavorites: (mealId: number) => Promise<any>;
  removeFromFavorites: (mealId: number) => Promise<any>;
  handleRefetch?: () => void;
  orientation: 'horizontal' | 'vertical';
  id: number;
  isFavorite: boolean;
  name: string;
  preparationTime: {
    minutes: number;
  };
  nutrition: {
    singleServing: {
      kcal: number;
    };
  };
  preparationImageUrl: string;
  tags: {
    dietTypeTags?: CategoryMealTag[];
    mealTypeTags?: CategoryMealTag[];
    customTags?: CategoryMealTag[];
  };
  tagsVisibility?: {
    dietTypeTagsVisibleOnHomepage: boolean;
    mealTypeTagsVisibleOnHomepage: boolean;
    customTagsVisibleOnHomepage: boolean;
  };
  rating?: {
    averageRating?: {
      averageRating: number;
    };
  };
  displayRating?: boolean;
}

export const CookbookMealItem: React.FC<ICookbookMealItem> = ({
  id,
  name,
  preparationImageUrl,
  tags,
  tagsVisibility,
  isFavorite,
  addToFavorites,
  removeFromFavorites,
  handleRefetch,
  orientation,
  rating,
  displayRating,
  preparationTime,
  nutrition,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess, dismiss } = useToast();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const {
    consts: { APP_BRAND },
    images: {
      cookbookFavouriteFull,
      cookbookFavouriteEmpty,
      favouriteFullSmall,
      favouriteEmptySmall,
      ratingStarFull,
      addToTrackerIcon,
    },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        const response = await removeFromFavorites(id);
        if (response.data.cookbook.removeFavoriteMeal.code === 200) {
          handleRefetch && (await handleRefetch());
          toastSuccess(
            <p className="mb-0">
              {t('cookbook.favorites.notification.removedFromFav.content')}
              <Button
                color="link"
                onClick={async () => {
                  await addToFavorites(id);
                  handleRefetch && (await handleRefetch());
                  dismiss();
                }}
              >
                {t('meal.favorites.notification.removedFromFav.button')}
              </Button>
            </p>
          );
        }
      } else {
        const response = await addToFavorites(id);
        if (response.data.cookbook.addFavoriteMeal.successCode === 200) {
          toastSuccess(
            <p className="mb-0">{t('cookbook.favorites.notification.addedToFav.content')}</p>
          );
        }
      }

      trackFirebaseEvent('user_action', {
        action: 'tap-add_favourites',
        location: 'Dietplan',
        meal_id: id,
        cookbook_meal_types: tags.mealTypeTags
          ? tags.mealTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        selected_diet: tags.dietTypeTags
          ? tags.dietTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        meals_tab: 'cookbook',
        meal_favourites: isFavorite,
      });
    } catch (err) {
      toastError();
    }
  };

  let coverImage;

  // image operations
  if (orientation === 'vertical') {
    coverImage = cloudflareImage(preparationImageUrl, `${APP_BRAND}`, 'cookbookMealItemVertical');
  } else {
    coverImage = cloudflareImage(
      preparationImageUrl,
      `${APP_BRAND}`,
      'cookbookMealItemHorizontal2'
    );
  }

  const renderTags = () => {
    const tagsArray: any[] = [];
    if (tagsVisibility) {
      if (tagsVisibility.customTagsVisibleOnHomepage) {
        tags.customTags &&
          tags.customTags.map((tag) => {
            tagsArray.push(
              <span
                className="tag me-1"
                key={`${tag.__typename}_${tag.id}`}
                style={{ backgroundColor: tag.labelColor, color: '#fff' }}
              >
                {tag.name}
              </span>
            );
            return null;
          });
      }
      if (tagsVisibility.mealTypeTagsVisibleOnHomepage) {
        tags.mealTypeTags &&
          tags.mealTypeTags.map((tag) => {
            tagsArray.push(
              <span className="tag me-1" key={`${tag.__typename}_${tag.id}`}>
                {tag.name}
              </span>
            );
            return null;
          });
      }
      if (tagsVisibility.dietTypeTagsVisibleOnHomepage) {
        tags.dietTypeTags &&
          tags.dietTypeTags.map((tag) => {
            tagsArray.push(
              <span className="tag me-1" key={`${tag.__typename}_${tag.id}`}>
                {tag.name}
              </span>
            );
            return null;
          });
      }
      return tagsArray;
    } else {
      return (
        <>
          {tags.customTags &&
            tags.customTags.map((tag) => (
              <span
                className="tag me-1"
                key={`${tag.__typename}_${tag.id}`}
                style={{ backgroundColor: tag.labelColor, color: '#fff' }}
              >
                {tag.name}
              </span>
            ))}
          {tags.mealTypeTags &&
            tags.mealTypeTags.map((tag) => (
              <span className="tag me-1" key={`${tag.__typename}_${tag.id}`}>
                {tag.name}
              </span>
            ))}
          {tags.dietTypeTags &&
            tags.dietTypeTags.map((tag) => (
              <span className="tag me-1" key={`${tag.__typename}_${tag.id}`}>
                {tag.name}
              </span>
            ))}
        </>
      );
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleAddMealToTracker = () => {
    const isTrackerEnabled = localStorage.getItem('trackerEnabled') === 'true';

    if (isTrackerEnabled) {
      navigate(`/meals/cookbook/module/tracker/add-meal/${id}`);
    } else {
      toggleModal();
    }
  };

  return (
    <>
      {orientation === 'vertical' ? (
        <div className="meal">
          <div className="meal-box cursor-pointer">
            <div
              className="meal-image"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              <img src={coverImage} width={612} height={384} className="img-fluid" alt={name} />
              {rating && rating.averageRating && displayRating && (
                <span className="tag tag-rating">
                  <img src={ratingStarFull} className="me-1" alt="" />
                  {rating.averageRating.averageRating}
                </span>
              )}
            </div>

            <div
              className="tags mb-2"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              {renderTags()}
            </div>

            <div
              role="button"
              className="meal-title mb-s"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              <h4 className="title-medium semi-bold mb-0">{name}</h4>
            </div>

            <div className="d-flex align-items-center justify-content-between">
              <span
                className="description text-text-variant-color"
                onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
              >
                {nutrition?.singleServing?.kcal}&nbsp;kcal
              </span>

              <Button
                color="primary"
                size="sm"
                className="p-xs z-index-999"
                style={{ minHeight: '32px', lineHeight: '0' }}
                onClick={handleAddMealToTracker}
              >
                <img src={addToTrackerIcon} alt="" />
              </Button>
            </div>
          </div>
          <button className="add-remove-fav" type="button" onClick={toggleFavorite}>
            <img
              src={isFavorite ? cookbookFavouriteFull : cookbookFavouriteEmpty}
              alt=""
              width="32"
              height="32"
            />
          </button>
        </div>
      ) : (
        <Col xs="12" md="6" className="meal horizontal">
          <div className="meal-box d-flex cursor-pointer">
            <div
              className="meal-image"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              <img src={coverImage} width={192} height={184} className="img-fluid" alt={name} />
            </div>
            <div className="meal-content">
              <div
                className="tags me-4"
                onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
              >
                {rating && rating.averageRating && (
                  <span className="tag tag-rating me-1">
                    <img src={ratingStarFull} className="me-1" alt="" />
                    {rating.averageRating.averageRating}
                  </span>
                )}

                {renderTags()}
              </div>
              <div
                role="button"
                className="meal-title mb-s"
                onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
              >
                <p className="semi-bold mb-0">{name}</p>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <span
                  className="description text-text-variant-color"
                  onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
                >
                  {nutrition?.singleServing?.kcal}&nbsp;kcal
                </span>

                <Button
                  color="primary"
                  size="sm"
                  className="p-xs z-index-999"
                  style={{ minHeight: '32px', lineHeight: '0' }}
                  onClick={handleAddMealToTracker}
                >
                  <img src={addToTrackerIcon} alt="" />
                </Button>
              </div>
            </div>

            <button className="add-remove-fav" type="button" onClick={toggleFavorite}>
              <img
                src={isFavorite ? favouriteFullSmall : favouriteEmptySmall}
                alt=""
                width="20"
                height="20"
              />
            </button>
          </div>
        </Col>
      )}
      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('tracker.logMealError.popup.title')}</h4>
          <p className="text-text-variant-color multi-line mb-0">
            {t('tracker.logMealError.popup.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            tag={Link}
            to="/tracker/settings"
            className="w-100"
            onClick={toggleModal}
          >
            {t('tracker.logMealError.popup.settingsButton')}
          </Button>
          <Button color="link" className="w-100" onClick={toggleModal}>
            {t('tracker.logMealError.popup.cancelButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CookbookMealItem;
